// src/pages/admin/components/SpecializedPaymentPlansPanel.jsx

import React from 'react';
import { AdminAPI } from '../../../api/admin';
import { Card, Actions, Btn, Field, Row2, Row3 } from '../ui';

export default function SpecializedPaymentPlansPanel({ propertyId, propertyType }) {
  const [plans, setPlans] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState('');
  const [showForm, setShowForm] = React.useState(false);
  const [editingPlan, setEditingPlan] = React.useState(null);

  // Определяем API методы в зависимости от типа объекта
  const getApiMethods = () => {
    switch (propertyType) {
      case 'foreign':
        return {
          list: AdminAPI.listForeignPaymentPlans,
          create: AdminAPI.createForeignPaymentPlan,
        };
      case 'suburban':
        return {
          list: AdminAPI.listSuburbanPaymentPlans,
          create: AdminAPI.createSuburbanPaymentPlan,
        };
      case 'commercial-rent':
        return {
          list: AdminAPI.listCommercialRentPaymentPlans,
          create: AdminAPI.createCommercialRentPaymentPlan,
        };
      case 'parking':
        return {
          list: AdminAPI.listParkingPaymentPlans,
          create: AdminAPI.createParkingPaymentPlan,
        };
      default:
        return {
          list: AdminAPI.listPaymentPlans,
          create: AdminAPI.createPaymentPlan,
        };
    }
  };

  const api = React.useMemo(() => getApiMethods(), [propertyType]);

  const [form, setForm] = React.useState({
    title_ru: '',
    title_en: '',
    description_ru: '',
    description_en: '',
    initial_payment_percent: '',
    installment_months: '',
    is_active: true,
  });

  const load = React.useCallback(async () => {
    if (busy) return; // Prevent concurrent requests
    setBusy(true);
    setMsg('');
    try {
      const resp = await api.list(propertyId);
      // Backend returns array directly, not nested in 'payment_plans' property
      setPlans(Array.isArray(resp) ? resp : []);
    } catch (e) {
      setMsg(e.message || 'Не удалось загрузить условия оплаты');
    } finally {
      setBusy(false);
    }
  }, [propertyId, propertyType, busy]);

  React.useEffect(() => { load(); }, [propertyId, propertyType]);

  const resetForm = () => {
    setForm({
      title_ru: '',
      title_en: '',
      description_ru: '',
      description_en: '',
      initial_payment_percent: '',
      installment_months: '',
      is_active: true,
    });
    setEditingPlan(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title_ru.trim()) {
      setMsg('Укажите название плана');
      return;
    }

    setBusy(true);
    try {
      const payload = {
        ...form,
        initial_payment_percent: form.initial_payment_percent ? Number(form.initial_payment_percent) : undefined,
        installment_months: form.installment_months ? Number(form.installment_months) : undefined,
      };

      if (editingPlan) {
        await AdminAPI.updatePaymentPlan(editingPlan.id, payload);
        setMsg('План оплаты обновлен');
      } else {
        await api.create(propertyId, payload);
        setMsg('План оплаты создан');
      }

      await load();
      resetForm();
      setShowForm(false);
      setTimeout(() => setMsg(''), 2000);
    } catch (e) {
      setMsg(e.message || 'Ошибка сохранения плана оплаты');
    } finally {
      setBusy(false);
    }
  };

  const handleEdit = (plan) => {
    setForm({
      title_ru: plan.title_ru || '',
      title_en: plan.title_en || '',
      description_ru: plan.description_ru || '',
      description_en: plan.description_en || '',
      initial_payment_percent: plan.initial_payment_percent || '',
      installment_months: plan.installment_months || '',
      is_active: plan.is_active ?? true,
    });
    setEditingPlan(plan);
    setShowForm(true);
  };

  const handleDelete = async (planId) => {
    if (!window.confirm('Удалить план оплаты?')) return;
    setBusy(true);
    try {
      await AdminAPI.deletePaymentPlan(planId);
      await load();
      setMsg('План оплаты удален');
      setTimeout(() => setMsg(''), 2000);
    } catch (e) {
      setMsg(e.message || 'Ошибка удаления плана оплаты');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h4 style={{ margin: 0 }}>Условия оплаты ({plans.length})</h4>
        <Actions style={{ margin: 0 }}>
          <Btn onClick={() => setShowForm(!showForm)} disabled={busy}>
            {showForm ? 'Отмена' : '+ Добавить план'}
          </Btn>
        </Actions>
      </div>

      {msg && <div style={{ color: msg.includes('Ошибка') ? '#e66' : '#1b5e20', marginBottom: 8 }}>{msg}</div>}

      {showForm && (
        <Card style={{ marginBottom: 16, background: '#f8f9fa' }}>
          <h5>{editingPlan ? 'Редактировать план оплаты' : 'Новый план оплаты'}</h5>
          <form onSubmit={handleSubmit}>
            <Row2>
              <Field>
                Название (RU) *
                <input
                  value={form.title_ru}
                  onChange={(e) => setForm(f => ({ ...f, title_ru: e.target.value }))}
                  placeholder="Рассрочка 50/50"
                  required
                />
              </Field>
              <Field>
                Название (EN)
                <input
                  value={form.title_en}
                  onChange={(e) => setForm(f => ({ ...f, title_en: e.target.value }))}
                  placeholder="50/50 Installment"
                />
              </Field>
            </Row2>

            <Row3>
              <Field>
                Первоначальный взнос, %
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={form.initial_payment_percent}
                  onChange={(e) => setForm(f => ({ ...f, initial_payment_percent: e.target.value }))}
                  placeholder="50"
                />
              </Field>
              <Field>
                Рассрочка, месяцев
                <input
                  type="number"
                  min="1"
                  value={form.installment_months}
                  onChange={(e) => setForm(f => ({ ...f, installment_months: e.target.value }))}
                  placeholder="12"
                />
              </Field>
              <Field>
                Активен
                <select
                  value={form.is_active}
                  onChange={(e) => setForm(f => ({ ...f, is_active: e.target.value === 'true' }))}
                >
                  <option value="true">Да</option>
                  <option value="false">Нет</option>
                </select>
              </Field>
            </Row3>

            <Row2>
              <Field>
                Описание (RU)
                <textarea
                  rows={3}
                  value={form.description_ru}
                  onChange={(e) => setForm(f => ({ ...f, description_ru: e.target.value }))}
                  placeholder="Подробное описание условий плана оплаты..."
                />
              </Field>
              <Field>
                Описание (EN)
                <textarea
                  rows={3}
                  value={form.description_en}
                  onChange={(e) => setForm(f => ({ ...f, description_en: e.target.value }))}
                  placeholder="Detailed description of payment plan terms..."
                />
              </Field>
            </Row2>

            <Actions>
              <Btn type="submit" $kind="primary" disabled={busy}>
                {editingPlan ? 'Сохранить' : 'Создать'}
              </Btn>
              <Btn type="button" onClick={() => { resetForm(); setShowForm(false); }}>
                Отмена
              </Btn>
            </Actions>
          </form>
        </Card>
      )}

      {busy && <div style={{ opacity: 0.6 }}>Загрузка...</div>}

      <div style={{ display: 'grid', gap: 12 }}>
        {plans.map((plan) => (
          <Card key={plan.id} style={{ background: '#f8f9fa' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <h5 style={{ margin: '0 0 8px 0' }}>
                  {plan.title_ru}
                  {!plan.is_active && <span style={{ color: '#e66', marginLeft: 8 }}>• Неактивен</span>}
                </h5>
                
                {plan.title_en && (
                  <div style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>
                    {plan.title_en}
                  </div>
                )}
                
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8, fontSize: 13, color: '#666', marginBottom: 8 }}>
                  {plan.initial_payment_percent && (
                    <div>💰 Первый взнос: {plan.initial_payment_percent}%</div>
                  )}
                  {plan.installment_months && (
                    <div>📅 Рассрочка: {plan.installment_months} мес.</div>
                  )}
                </div>
                
                {plan.description_ru && (
                  <div style={{ fontSize: 13, color: '#777' }}>
                    {plan.description_ru}
                  </div>
                )}
              </div>
              
              <Actions style={{ margin: 0 }}>
                <Btn onClick={() => handleEdit(plan)} disabled={busy}>
                  Редактировать
                </Btn>
                <Btn onClick={() => handleDelete(plan.id)} disabled={busy} style={{ color: '#e66' }}>
                  Удалить
                </Btn>
              </Actions>
            </div>
          </Card>
        ))}
      </div>

      {plans.length === 0 && !busy && (
        <div style={{ textAlign: 'center', padding: 20, color: '#777' }}>
          Условия оплаты не добавлены. Нажмите "Добавить план" для создания.
        </div>
      )}
    </Card>
  );
}